import React, { useEffect, useState } from 'react';
import { ArrowLeft, Check, ExternalLink, Loader2, Save } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { PrimaryButton, ToolButton } from '../dash/Toolbar';
import { Tag } from '../dash/ListRows';
import { AutoTextarea } from './fields';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';

// Full-bleed editor frame shared by the blog post and case study editors:
// a Catalogue-style 56px bar (back, breadcrumb, save state, actions), a
// writing canvas on the left and a settings inspector on the right.

export type EditorStatus = 'draft' | 'published';

export const StatusTag: React.FC<{ status: string }> = ({ status }) =>
  status === 'published' ? <Tag color="var(--oa-accent)">Published</Tag> : <Tag color="var(--oa-muted)">Draft</Tag>;

/** True when the serialised document differs from the last saved copy. */
export const useDirty = (doc: unknown, savedSnapshot: string | null) => {
  const current = JSON.stringify(doc);
  return savedSnapshot !== null && current !== savedSnapshot;
};

type Props = {
  section: string;
  kind: string;
  title: string;
  status: EditorStatus;
  dirty: boolean;
  saving: boolean;
  loading?: boolean;
  previewHref?: string;
  onBack: () => void;
  onSaveDraft: () => void;
  onPublish: () => void;
  inspector: React.ReactNode;
  children: React.ReactNode;
};

export const EditorShell: React.FC<Props> = ({
  section,
  kind,
  title,
  status,
  dirty,
  saving,
  loading,
  previewHref,
  onBack,
  onSaveDraft,
  onPublish,
  inspector,
  children,
}) => {
  const [confirmLeave, setConfirmLeave] = useState(false);

  // Cmd/Ctrl+S saves without changing the publish state.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (!saving) onSaveDraft();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onSaveDraft, saving]);

  // Warn before a tab close or reload drops unsaved work.
  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  const back = () => (dirty ? setConfirmLeave(true) : onBack());
  const published = status === 'published';

  return (
    <div className="flex h-full flex-col bg-[color:var(--oa-card)] font-poppins text-[color:var(--oa-text)] antialiased">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] px-4">
        <ToolButton onClick={back} aria-label={`Back to ${section}`} className="px-2">
          <ArrowLeft className="h-4 w-4" />
        </ToolButton>
        <nav aria-label="Breadcrumb" className="flex min-w-0 items-baseline gap-1.5 text-[13px]">
          <button type="button" onClick={back} className="shrink-0 text-[color:var(--oa-muted)] hover:text-[color:var(--oa-text)]">
            {section}
          </button>
          <span className="text-[color:var(--oa-faint)]">/</span>
          <span className="shrink-0 text-[color:var(--oa-muted)]">{kind}</span>
          <span className="text-[color:var(--oa-faint)]">/</span>
          <h1 className="truncate font-montserrat text-[15px] font-extrabold tracking-[-0.01em] text-[color:var(--oa-text)]">{title || 'Untitled'}</h1>
        </nav>
        <StatusTag status={status} />
        <span className={cn('hidden items-center gap-1.5 text-[11.5px] sm:inline-flex', dirty ? 'text-[color:var(--oa-warn)]' : 'text-[color:var(--oa-muted)]')}>
          <span className={cn('h-1.5 w-1.5 rounded-full', dirty ? 'bg-[color:var(--oa-warn)]' : 'bg-[color:var(--oa-accent)]')} />
          {loading ? 'Loading…' : saving ? 'Saving…' : dirty ? 'Unsaved changes' : 'All changes saved'}
        </span>

        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {previewHref ? (
            <ToolButton onClick={() => window.open(previewHref, '_blank', 'noopener')} title="Open the live page in a new tab">
              <ExternalLink className="h-4 w-4" />
              <span className="hidden md:inline">View</span>
            </ToolButton>
          ) : null}
          <ToolButton onClick={onSaveDraft} disabled={saving || loading} title="Save (⌘S)">
            <Save className="h-4 w-4" />
            {published ? 'Save' : 'Save draft'}
          </ToolButton>
          <PrimaryButton onClick={onPublish} disabled={saving || loading}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {published ? 'Update' : 'Publish'}
          </PrimaryButton>
        </div>
      </header>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-[color:var(--oa-muted)]">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-auto lg:flex-row lg:overflow-hidden">
          <div className="min-w-0 flex-1 bg-[color:var(--oa-raised)] lg:overflow-auto">
            <div className="mx-auto w-full max-w-[780px] px-5 pb-24 pt-10 md:px-8">{children}</div>
          </div>
          <aside aria-label={`${kind} settings`} className="w-full shrink-0 border-t border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] lg:w-[320px] lg:overflow-auto lg:border-l lg:border-t-0">
            {inspector}
          </aside>
        </div>
      )}

      <AlertDialog open={confirmLeave} onOpenChange={setConfirmLeave}>
        <AlertDialogContent className="rounded-xl font-poppins">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-montserrat text-[16px] font-extrabold text-[color:var(--oa-text)]">Leave without saving?</AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] text-[color:var(--oa-muted)]">Your changes to this {kind.toLowerCase()} have not been saved and will be lost.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-8 rounded-md text-[12.5px]">Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={onBack} className="h-8 rounded-md bg-[color:var(--oa-bad)] text-[12.5px] text-white hover:bg-[color:var(--oa-bad)]">
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

/** Headline title + standfirst inputs at the top of the canvas. */
export const CanvasTitle: React.FC<{
  eyebrow?: React.ReactNode;
  title: string;
  onTitle: (v: string) => void;
  titlePlaceholder: string;
  sub?: React.ReactNode;
}> = ({ eyebrow, title, onTitle, titlePlaceholder, sub }) => (
  <div className="mb-8">
    {eyebrow ? <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--oa-accent)]">{eyebrow}</p> : null}
    <AutoTextarea
      bare
      minRows={1}
      value={title}
      onChange={(e) => onTitle(e.target.value.replace(/\n/g, ' '))}
      onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
      placeholder={titlePlaceholder}
      aria-label="Title"
      className="font-montserrat text-[32px] font-extrabold leading-tight tracking-[-0.02em] text-[color:var(--oa-text)] placeholder:text-[color:var(--oa-faint)] md:text-[38px]"
    />
    {sub}
  </div>
);

/** A titled white card on the canvas. */
export const CanvasSection: React.FC<{ title: React.ReactNode; description?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode; className?: string }> = ({
  title,
  description,
  actions,
  children,
  className,
}) => (
  <section className={cn('mb-5 rounded-xl border border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)]', className)}>
    <header className="flex items-start justify-between gap-3 px-5 pb-1 pt-4">
      <div className="min-w-0">
        <h2 className="font-montserrat text-[14px] font-bold tracking-[-0.01em] text-[color:var(--oa-text)]">{title}</h2>
        {description ? <p className="mt-0.5 text-[12px] text-[color:var(--oa-muted)]">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-1">{actions}</div> : null}
    </header>
    <div className="px-5 pb-5 pt-3">{children}</div>
  </section>
);
